import axios from 'axios';
import {apiUrl} from '@/env';
import {IUserProfile, IUserProfileList, IUserStats} from '@/interfaces/user';
import {IForm, IFormBatch, IFormValues, IFormValuesList, IFormBatchStats, IFormCategory} from '@/interfaces/form';
import {IRole, IRoleSettings} from '@/interfaces/role';
import {IExternalData, IExternalDataRowsList} from '@/interfaces/external-data';
import {ISearchEngine} from '@/interfaces/search_engine';
import {IExternalUser} from '@/interfaces/external_user';
import {IFileUpload} from '@/interfaces/file';
import {IEmailTemplate} from '@/interfaces/email-template';
import {IPrinterLog, IPrinterLogList} from '@/interfaces/printer-logs';
import {IAccessLog, IAccessLogList} from '@/interfaces/access-log';
import {IDynamicQuery} from '@/interfaces';
import {IGroup} from '@/interfaces/group';
import {IPermission} from '@/interfaces/permission';
import {axiosDelete, get as axiosGet} from '@/helpers/api/utils';
import {IInstitution} from '@/interfaces/institution';
import {IAddress} from '@/interfaces/address';
import {getLocalToken} from '@/utils';


function authHeaders(token: string, params: any = {}) {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  };
}

function appendDefinedParam(params: URLSearchParams, key: string, value: any) {
  if (value === undefined || value === null || value === '') {
    return;
  }
  if (typeof value === 'string' && ['null', 'undefined'].includes(value.trim())) {
    return;
  }
  params.append(String(key), String(value));
}

function toUrlSearchParams(payload?: any) {
  const params = new URLSearchParams();
  if (payload) {
    Object.keys(payload).forEach((key) => appendDefinedParam(params, key, payload[key]));
  }
  return params;
}

export const api = {
  async logInGetToken(username: string, password: string) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);

    return axios.post(`${apiUrl}/api/v1/login/access-token/`, params);
  },
  async getMe() {
    return axiosGet<IUserProfile>('users/me/');
  },
  async getMeStats() {
    return axiosGet<IUserStats>('users/me/stats/');
  },
  async updateMe(token: string, data: IUserProfile) {
    return axios.put<IUserProfile>(`${apiUrl}/api/v1/users/me/`, data, authHeaders(token));
  },
  async getUser(userId: number) {
    return axiosGet<IUserProfile>(`users/${userId}/`);
  },
  async getUsers(payload?: IDynamicQuery) {
    const params = toUrlSearchParams(payload);
    return axiosGet<IUserProfileList>('users/', params);
  },
  async updateUser(token: string, userId: number, data: IUserProfile) {
    return axios.put(`${apiUrl}/api/v1/users/${userId}/`, data, authHeaders(token));
  },
  async createUser(token: string, data: IUserProfile, externalUserId: number = 0) {
    if (externalUserId > 0) {
      const params = new URLSearchParams();
      params.append('external_user_id', externalUserId as unknown as string);
      return axios.post(`${apiUrl}/api/v1/users/`, data, authHeaders(token, params));
    }
    return axios.post(`${apiUrl}/api/v1/users/`, data, authHeaders(token));
  },
  async deleteUser(userId: number) {
    return axiosDelete(`users/${userId}/`);
  },
  async passwordRecovery(email: string) {
    return axios.post(`${apiUrl}/api/v1/password-recovery/${email}/`);
  },
  async resetPassword(password: string, token: string) {
    return axios.post(`${apiUrl}/api/v1/reset-password/`, {
      new_password: password,
      token,
    });
  },


  async getForm(formID: number) {
    return axiosGet<IForm>(`forms/${formID}/`);
  },
  async getForms(payload?: { just_enabled: boolean }) {
    return axiosGet<IForm[]>(`forms/`, payload);
  },
  async getFormsAdmin(payload?: { just_enabled: boolean }) {
    return axiosGet<IForm[]>(`forms/admin/`, payload);
  },
  async registerForm(token: string, data: IForm) {
    return axios.post(`${apiUrl}/api/v1/forms/`, data, authHeaders(token));
  },
  async updateForm(token: string, data: IForm) {
    // return axios.put<IForm>(`${apiUrl}/api/v1/forms/`, data, authHeaders(token));
    return axios.put(`${apiUrl}/api/v1/forms/${data.id}/`, data, authHeaders(token));
  },
  async deleteForm(formID: number) {
    // return axios.put<IForm>(`${apiUrl}/api/v1/forms/`, data, authHeaders(token));
    return axiosDelete(`forms/${formID}/`);
  },

  async registerFormValuesInBatch(token: string, data: IFormValues[]) {
    return axios.post(`${apiUrl}/api/v1/forms-values/register-in-batch`, data, authHeaders(token));
  },
  async registerFormValues(token: string, data: IFormValues) {
    // const formData = new FormData();
    // formData.append('form_id', data.form_id as unknown as string)
    // formData.append('version', data.version as unknown as string)

    return axios.post(`${apiUrl}/api/v1/forms-values/`, data, authHeaders(token));
  },
  async updateFormValues(token: string, data: IFormValues) {
    return axios.put(`${apiUrl}/api/v1/forms-values/${data.id}/`, data, authHeaders(token));
  },
  async deleteFormValues(formValuesId: number) {
    return axiosDelete(`forms-values/${formValuesId}/`);
  },
  async getFormValuesByID(id: number) {
    return axiosGet<IFormValues>(`forms-values/${id}/`);
  },
  async getFormsValues(payload: { formID: number, formValueId?: number }) {
    const params = new URLSearchParams();
    if (payload.formValueId) {
      params.append('form_value_id', String(payload.formValueId));
    }
    return axiosGet<IFormValuesList>(`forms-values/by-form/${payload.formID}/`, params);
  },
  async getFormsValuesByQuery(payload: {
    skip: number,
    limit: number,
    formID: number,
    query_builder?: any,
    is_downloaded?: boolean,
    format?: string,
  }) {
    const params = new URLSearchParams();
    if (payload.query_builder) {
      params.append('query_builder', JSON.stringify(payload.query_builder));
    }
    if (payload.format) {
      params.append('format', payload.format);
    }
    params.append('is_downloaded', String(Boolean(payload.is_downloaded)));
    params.append('skip', String(payload.skip));
    params.append('limit', String(payload.limit));

    if (payload.is_downloaded) {
      return axios.get(`${apiUrl}/api/v1/forms-values/query/${payload.formID}/`, {
        ...authHeaders(getLocalToken() || '', params),
        responseType: 'blob',
      });
    }
    return axiosGet<IFormValuesList>(`forms-values/query/${payload.formID}/`, params);
  },
  async getFormsValuesWithInterval(payload: { skip: number, limit: number, formID: number }) {
    return axiosGet<IFormValuesList>(`forms-values/${payload.formID}/?skip=${payload.skip}&limit=${payload.limit}`);
  },
  async getFormsFile(fileID: number) {
    return axiosGet<IFileUpload>(`forms-values/form-files/${fileID}/`);
  },
  formFileDownloadUrl(fileID: number) {
    return `${apiUrl}/api/v1/forms-values/form-files/${fileID}/download/`;
  },
  async downloadFormFile(token: string, fileID: number) {
    return axios.get(`${apiUrl}/api/v1/forms-values/form-files/${fileID}/download/`, {
      ...authHeaders(token),
      responseType: 'blob',
    });
  },
  async uploadFormFile(token: string, formValueId: number, controlId: string, fileInfo: IFileUpload) {
    const formData = new FormData();
    formData.append('control_id', controlId);
    formData.append('idempotency_key', fileInfo.idempotency_key || `${controlId}-${fileInfo.name}-${fileInfo.original_size}`);
    if (fileInfo.form_files_id) {
      formData.append('replace_form_file_id', String(fileInfo.form_files_id));
    }
    formData.append('file', fileInfo.file as File);
    return axios.post<IFileUpload>(`${apiUrl}/api/v1/forms-values/${formValueId}/files/`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  async getRole(roleId: number) {
    return axiosGet<IRole>(`roles/${roleId}/`);
  },
  async getDefaultRoleSettings() {
    return axiosGet<IRoleSettings>(`roles/default-settings/`);
  },
  async getRoles() {
    const params = new URLSearchParams();
    params.append('limit', '-1');
    return axiosGet<IRole[]>(`roles/`, params);
  },
  async createRole(token: string, data: IRole) {
    return axios.post(`${apiUrl}/api/v1/roles/`, data, authHeaders(token));
  },
  async updateRole(token: string, data: IRole) {
    return axios.put(`${apiUrl}/api/v1/roles/${data.id}/`, data, authHeaders(token));
  },
  async deleteRole(roleId: number) {
    return axiosDelete(`roles/${roleId}/`);
  },

  async getExternalData(id: number) {
    return axiosGet<IExternalData>(`external-data/${id}/`);
  },
  async getExternalDataList() {
    return axiosGet<IExternalData[]>(`external-data/`);
  },
  async getExternalDataRows(id: number, payload: any = {}) {
    const params = toUrlSearchParams(payload);
    return axiosGet<IExternalDataRowsList>(`external-data/${id}/rows/`, params);
  },
  async downloadExternalData(token: string, id: number, format: string, payload: any = {}) {
    const params = toUrlSearchParams(payload);
    appendDefinedParam(params, 'format', format);
    return axios.get(`${apiUrl}/api/v1/external-data/${id}/export/`, {
      ...authHeaders(token, params),
      responseType: 'blob',
    });
  },
  async lookupExternalDataRow(id: number, field: string, value: string) {
    const params = new URLSearchParams();
    params.append('field', field);
    params.append('value', value);
    return axiosGet<IExternalDataRowsList>(`external-data/${id}/rows/lookup/`, params);
  },
  async externalDataFileUpload(token: string, data: any) {
    const formData = new FormData();
    formData.append('file', data);
    return axios.post<IExternalData>(`${apiUrl}/api/v1/external-data/upload-file/`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data/',
      },
    });
  },
  async createExternalData(token: string, data: IExternalData) {
    return axios.post(`${apiUrl}/api/v1/external-data/`, data, authHeaders(token));
  },
  async importExternalDataFile(token: string, data: IExternalData, file: any) {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('file', file);
    return axios.post(`${apiUrl}/api/v1/external-data/import-file/`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data/',
      },
    });
  },
  async updateExternalData(token: string, data: IExternalData) {
    return axios.put(`${apiUrl}/api/v1/external-data/${data.id}/`, data, authHeaders(token));
  },
  async replaceExternalDataFile(token: string, data: IExternalData, file: any) {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('file', file);
    return axios.put(`${apiUrl}/api/v1/external-data/${data.id}/import-file/`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data/',
      },
    });
  },
  async deleteExternalData(id: number) {
    return axiosDelete(`external-data/${id}/`);
  },

  async getSearchEngine(id: number) {
    return axiosGet<ISearchEngine>(`form-search-engines/${id}/`);
  },
  async getSearchEngines() {
    return axiosGet<ISearchEngine[]>(`form-search-engines/`);
  },
  async createSearchEngine(token: string, data: ISearchEngine) {
    return axios.post(`${apiUrl}/api/v1/form-search-engines/`, data, authHeaders(token));
  },
  async updateSearchEngine(token: string, data: ISearchEngine) {
    return axios.put(`${apiUrl}/api/v1/form-search-engines/${data.id}/`, data, authHeaders(token));
  },
  async deleteSearchEngine(id: number) {
    return axiosDelete(`form-search-engines/${id}/`);
  },

  async getFormBatch(formID: number) {
    return axiosGet<IFormBatch>(`forms-batch/${formID}/`);
  },
  async getFormBatchesAdmin(payload: { with_stats: boolean, skip: number, limit: number, query_value?: string }) {
    const params = new URLSearchParams();
    if (payload.query_value) {
      params.append('query_value', payload.query_value);
    }
    return axiosGet<IFormBatchStats>(
      `forms-batch/admin/?skip=${payload.skip}&limit=${payload.limit}&with_stats=${payload.with_stats}`,
      params,
    );
  },
  async getFormBatches(payload: { with_stats: boolean, skip: number, limit: number, query_value?: string }) {
    const params = new URLSearchParams();
    if (payload.query_value) {
      params.append('query_value', payload.query_value);
    }
    return axiosGet<IFormBatchStats>(`forms-batch/?skip=${payload.skip}&limit=${payload.limit}&with_stats=${payload.with_stats}`, params);
  },
  async createFormBatch(token: string, data: IFormBatch) {
    return axios.post(`${apiUrl}/api/v1/forms-batch/`, data, authHeaders(token));
  },
  async updateFormBatch(token: string, data: IFormBatch) {
    return axios.put(`${apiUrl}/api/v1/forms-batch/${data.id}/`, data, authHeaders(token));
  },
  async deleteFormBatch(formID: number) {
    return axiosDelete(`forms-batch/${formID}/`);
  },


  async actionRegisterExternalUser(data: IExternalUser) {
    return axios.post(`${apiUrl}/api/v1/external-users/`, data);
  },
  async getExternalUser(userId: number) {
    return axiosGet<IExternalUser>(`external-users/${userId}/`);
  },
  async getExternalUsers() {
    return axiosGet<IExternalUser[]>(`external-users/`);
  },
  async deleteExternalUser(userId: number) {
    return axiosDelete(`external-users/${userId}/`);
  },

  async createPrinterLog(token: string, data: IPrinterLog) {
    return axios.post<IPrinterLog>(`${apiUrl}/api/v1/printer-logs/`, data);
  },
  async getPrinterLogs(payload?: IDynamicQuery) {
    const params = toUrlSearchParams(payload);
    return axiosGet<IPrinterLogList>(`printer-logs/`, params);
  },
  async getPrinterLogsValidation(authorizationCode: string) {
    return axiosGet<IPrinterLog>(`printer-logs/${authorizationCode}/`);
  },
  async deletePrinterLog(id: number) {
    return axiosDelete(`printer-logs/${id}/`);
  },

  async sentDirectMail(token: string, data: IEmailTemplate) {
    return axios.post(`${apiUrl}/api/v1/utils/direct-mail/`, data, authHeaders(token));
  },

  async createAccessLog(token: string, data: IAccessLog) {
    return axios.post<IAccessLog>(`${apiUrl}/api/v1/access-log/`, data, authHeaders(token));
  },
  async getAccessLog(id: number) {
    return axiosGet<IAccessLog>(`access-log/${id}/`);
  },
  async getAccessLogs(token: string, payload: IDynamicQuery) {
    const params = toUrlSearchParams(payload);
    return axiosGet<IAccessLogList>(`access-log/`, params);
  },

  // Group
  async getGroup(groupId: number) {
    return axiosGet<IGroup>(`groups/${groupId}/`);
  },
  async getGroups() {
    return axiosGet<IGroup[]>(`groups/`);
  },
  async createGroup(token: string, data: IGroup) {
    return axios.post(`${apiUrl}/api/v1/groups/`, data, authHeaders(token));
  },
  async updateGroup(token: string, data: IGroup) {
    return axios.put(`${apiUrl}/api/v1/groups/${data.id}/`, data, authHeaders(token));
  },
  async deleteGroup(id: number) {
    return axiosDelete(`groups/${id}/`);
  },

  // Permissions
  async getPermissions() {
    return axiosGet<IPermission[]>(`permissions/`);
  },

  // Form Categories
  async getFormCategory(id: number) {
    return axiosGet<IFormCategory>(`form-category/${id}/`);
  },
  async getFormCategories() {
    return axiosGet<IFormCategory[]>(`form-category/`);
  },
  async createFormCategory(token: string, data: IFormCategory) {
    return axios.post(`${apiUrl}/api/v1/form-category/`, data, authHeaders(token));
  },
  async updateFormCategory(token: string, data: IFormCategory) {
    return axios.put(`${apiUrl}/api/v1/form-category/${data.id}/`, data, authHeaders(token));
  },
  async deleteFormCategory(id: number) {
    return axiosDelete(`form-category/${id}/`);
  },

  // Institution
  async getInstitution(id: number) {
    return axiosGet<IInstitution>(`institution/${id}/`);
  },
  async getInstitutions() {
    return axiosGet<IInstitution[]>(`institution/`);
  },
  async createInstitution(token: string, data: IInstitution) {
    return axios.post(`${apiUrl}/api/v1/institution/`, data, authHeaders(token));
  },
  async updateInstitution(token: string, data: IInstitution) {
    return axios.put(`${apiUrl}/api/v1/institution/${data.id}/`, data, authHeaders(token));
  },
  async deleteInstitution(id: number) {
    return axiosDelete(`institution/${id}/`);
  },

  async getAddressFromCEP(cep: string) {
    cep = cep.replace(/[^\d]+/g, '');
    return axios.get<IAddress>(`https://viacep.com.br/ws/${cep}/json/`);
  },
};
